import { AttributeModel as AttributeApi } from '@/http/models/attribute/Attribute'
import { AttributeValueModel as AttributeValueApi } from '@/http/models/attribute/AttributeValue'
import { toast } from 'sonner'

import { useState } from 'react'

import { AttributeModel } from '@/types'
import { AttributeValueModel } from '@/types/model/AttributeValue.ts'

import { useFetchAttributes } from './useFetchAttributes'

type DialogMode = 'create' | 'edit' | 'view'

export const useAttributesTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [valueMode, setValueMode] = useState<DialogMode>('create')
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false)
  const [attributeValueDialogOpen, setAttributeValueDialogOpen] = useState(false)
  const [createAttributeValue, setCreateAttributeValue] =
    useState<Partial<AttributeValueModel.AttributeValue> | null>(null)
  const [editAttribute, setEditAttribute] = useState<AttributeModel.Attribute | null>(null)
  const [editAttributeValue, setEditAttributeValue] =
    useState<AttributeValueModel.AttributeValue | null>(null)
  const [selectedAttribute, setSelectedAttribute] = useState<AttributeModel.Attribute | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteValueDialogOpen, setDeleteValueDialogOpen] = useState(false)
  const [attributeToDelete, setAttributeToDelete] = useState<AttributeModel.Attribute | null>(null)
  const [attributeValueToDelete, setAttributeValueToDelete] =
    useState<AttributeValueModel.AttributeValue | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formMethods, setPage, setPageSize, refresh } =
    useFetchAttributes({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditAttribute(null)
    setAttributeDialogOpen(true)
  }

  const openCreateValueDialog = (attribute: AttributeModel.Attribute) => {
    setValueMode('create')
    setSelectedAttribute(attribute)
    setCreateAttributeValue({ attribute_id: attribute._id })
    setEditAttributeValue(null)
    setAttributeValueDialogOpen(true)
  }

  const openViewDialog = (attribute: AttributeModel.Attribute) => {
    setMode('view')
    setEditAttribute(attribute)
    setAttributeDialogOpen(true)
  }

  const openViewValueDialog = (attributeValue: AttributeValueModel.AttributeValue) => {
    setValueMode('view')
    setEditAttributeValue(attributeValue)
    setAttributeValueDialogOpen(true)
  }

  const openEditDialog = (attribute: AttributeModel.Attribute) => {
    setMode('edit')
    setEditAttribute(attribute)
    setAttributeDialogOpen(true)
  }

  const openEditValueDialog = (attributeValue: AttributeValueModel.AttributeValue) => {
    setValueMode('edit')
    const parentAttr = data.find((a) => a._id === attributeValue.attribute_id)
    setSelectedAttribute(parentAttr || null)
    setEditAttributeValue(attributeValue)
    setAttributeValueDialogOpen(true)
  }

  const closeDialog = () => {
    setAttributeDialogOpen(false)
    setEditAttribute(null)
  }

  const closeValueDialog = () => {
    setAttributeValueDialogOpen(false)
    setEditAttributeValue(null)
    setSelectedAttribute(null)
  }

  const handleDeleteClick = (attribute: AttributeModel.Attribute) => {
    setAttributeToDelete(attribute)
    setDeleteDialogOpen(true)
  }

  const handleDeleteValueClick = (attributeValue: AttributeValueModel.AttributeValue) => {
    setAttributeValueToDelete(attributeValue)
    setDeleteValueDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!attributeToDelete?._id) return

    setDeleteLoading(true)
    const response = await AttributeApi.delete({
      params: { id: attributeToDelete._id },
    })

    if (response.success) {
      toast.success('Attribute deleted successfully')
      setDeleteDialogOpen(false)
      setAttributeToDelete(null)
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to delete attribute')
    }
    setDeleteLoading(false)
  }

  const handleDeleteValueConfirm = async () => {
    if (!attributeValueToDelete?._id) return

    setDeleteLoading(true)
    const response = await AttributeValueApi.delete({
      params: { id: attributeValueToDelete._id },
    })

    if (response.success) {
      toast.success('Attribute value deleted successfully')
      setDeleteValueDialogOpen(false)
      setAttributeValueToDelete(null)
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to delete attribute value')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (attribute: AttributeModel.Attribute) => {
    const newStatus = attribute.status === 'active' ? 'inactive' : 'active'

    const response = await AttributeApi.update({
      params: { id: attribute._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Attribute status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateAttribute = async (data: any) => {
    setCreateLoading(true)
    const response = await AttributeApi.create({
      body: data,
    })

    if (response.success) {
      toast.success('Attribute created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to create attribute')
    }
    setCreateLoading(false)
  }

  const handleCreateAttributeValue = async (data: any) => {
    setCreateLoading(true)
    const response = await AttributeValueApi.create({
      body: data,
    })

    if (response.success) {
      toast.success('Attribute value created successfully')
      closeValueDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to create attribute value')
    }
    setCreateLoading(false)
  }

  const handleUpdateAttribute = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await AttributeApi.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Attribute updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to update attribute')
    }
    setUpdateLoading(false)
  }

  const handleUpdateAttributeValue = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await AttributeValueApi.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Attribute value updated successfully')
      closeValueDialog()
      refresh()
    } else {
      toast.error((response.error?.data as any)?.message || 'Failed to update attribute value')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalAttributes: pagination.totalCount,
    activeAttributes: data.filter((attr) => attr.status === 'active').length,
    inactiveAttributes: data.filter((attr) => attr.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    mode,
    valueMode,
    attributeDialogOpen,
    attributeValueDialogOpen,
    createAttributeValue,
    editAttribute,
    editAttributeValue,
    deleteDialogOpen,
    deleteValueDialogOpen,
    attributeToDelete,
    attributeValueToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    selectedAttribute,
    setPage,
    setPageSize,
    openCreateDialog,
    openCreateValueDialog,
    openViewDialog,
    openViewValueDialog,
    openEditDialog,
    openEditValueDialog,
    closeDialog,
    closeValueDialog,
    handleDeleteClick,
    handleDeleteValueClick,
    handleDeleteConfirm,
    handleDeleteValueConfirm,
    setDeleteDialogOpen,
    setDeleteValueDialogOpen,
    handleCreateAttribute,
    handleCreateAttributeValue,
    handleUpdateAttribute,
    handleUpdateAttributeValue,
    toggleStatus,
  }
}
